"use client";

import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import Link from "next/link";
import AllToolsModal from "./AllToolsModal";
import { trackFunnel } from "@/libs/analytics";
import {
  getCatalogNavItems,
  isCatalogNavActive,
  type CatalogNavItem,
} from "@/components/dashboard/catalog/catalogNav";

interface ToolsSidebarV2Props {
  currentToolId: string;
  className?: string;
  dockMobileToBottom?: boolean;
}

const SCROLL_KEY = "toolsSidebarScroll";
const MOBILE_SCROLL_KEY = "toolsSidebarScrollX";

export default function ToolsSidebarV2({
  currentToolId,
  className,
  dockMobileToBottom = false,
}: ToolsSidebarV2Props) {
  const [showAllTools, setShowAllTools] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const desktopScrollRef = useRef<HTMLDivElement>(null);
  const mobileScrollRef = useRef<HTMLDivElement>(null);
  const items = useMemo(() => getCatalogNavItems(), []);

  useEffect(() => {
    const el = desktopScrollRef.current;
    if (!el) return;
    const saved = sessionStorage.getItem(SCROLL_KEY);
    if (saved) el.scrollTop = Number(saved);
  }, []);

  useEffect(() => {
    const el = mobileScrollRef.current;
    if (!el) return;
    const saved = sessionStorage.getItem(MOBILE_SCROLL_KEY);
    if (saved) el.scrollLeft = Number(saved);
    setCanScrollLeft(el.scrollLeft > 4);
  }, []);

  const handleDesktopScroll = useCallback(() => {
    if (desktopScrollRef.current) {
      sessionStorage.setItem(SCROLL_KEY, String(desktopScrollRef.current.scrollTop));
    }
  }, []);

  const updateMobileFades = useCallback(() => {
    const el = mobileScrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
  }, []);

  const handleMobileScroll = useCallback(() => {
    const el = mobileScrollRef.current;
    if (!el) return;
    sessionStorage.setItem(MOBILE_SCROLL_KEY, String(el.scrollLeft));
    updateMobileFades();
  }, [updateMobileFades]);

  const trackSwitch = (item: CatalogNavItem) => {
    trackFunnel("tool_engagement", "tool_switched", {
      from_tool: currentToolId,
      to_tool: item.id,
      to_tool_name: item.title,
      editor_version: "v2",
    });
  };

  const pillClass = (isActive: boolean) =>
    `inline-flex shrink-0 items-center gap-1.5 h-8 pl-1 pr-2.5 rounded-full border transition-colors ${
      isActive
        ? "border-primary bg-primary/10 text-primary"
        : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
    }`;

  return (
    <>
      <div
        className={`w-full md:w-[70px] flex-shrink-0 bg-white relative overflow-hidden ${
          dockMobileToBottom
            ? "h-auto md:h-full border-t border-gray-200 md:border-t-0 md:border-r shadow-[0_-4px_14px_rgba(0,0,0,0.06)] md:shadow-none"
            : "h-auto md:h-full border-b-0 md:border-r border-gray-200"
        } ${className || ""}`}
      >
        {/* —— Mobile: compact horizontal pills —— */}
        <div className="relative md:hidden">
          {canScrollLeft && (
            <div
              className="pointer-events-none absolute left-0 top-0 bottom-0 w-8 z-[1] bg-gradient-to-r from-white to-transparent"
              aria-hidden
            />
          )}
          <div
            className="pointer-events-none absolute right-0 top-0 bottom-0 w-8 z-[1] bg-gradient-to-l from-white to-transparent"
            aria-hidden
          />

          <div
            ref={mobileScrollRef}
            onScroll={handleMobileScroll}
            className="flex items-center gap-1.5 overflow-x-auto px-2 py-1.5 sidebar-scroll-none"
            style={{ msOverflowStyle: "none", scrollbarWidth: "none" }}
          >
            <button
              type="button"
              onClick={() => setShowAllTools(true)}
              className={pillClass(showAllTools)}
              title="View All Tools"
            >
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-600">
                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                  />
                </svg>
              </span>
              <span className="text-xs font-medium whitespace-nowrap">All tools</span>
            </button>

            {items.map((item) => {
              const isActive = isCatalogNavActive(item, currentToolId);
              const inner = (
                <>
                  <span className="relative h-6 w-6 shrink-0 overflow-hidden rounded-full bg-gray-100">
                    {item.poster ? (
                      <img src={item.poster} alt="" className="h-full w-full object-cover object-top" />
                    ) : (
                      <span className="flex h-full w-full items-center justify-center text-[10px] font-bold text-gray-400">
                        {item.title.slice(0, 1)}
                      </span>
                    )}
                  </span>
                  <span className="text-xs font-medium whitespace-nowrap max-w-[7.5rem] truncate">
                    {item.title}
                  </span>
                  {item.badge === "free" && (
                    <span className="rounded bg-[#69C85A] px-1 py-px text-[8px] font-bold uppercase leading-none text-white">
                      Free
                    </span>
                  )}
                </>
              );

              if (isActive) {
                return (
                  <div key={item.id} className={pillClass(true)} title={`${item.title} (active)`}>
                    {inner}
                  </div>
                );
              }

              return (
                <Link
                  key={item.id}
                  href={item.href}
                  title={`Open ${item.title}`}
                  onClick={() => trackSwitch(item)}
                  className={pillClass(false)}
                >
                  {inner}
                </Link>
              );
            })}
          </div>
        </div>

        {/* —— Desktop: vertical icon rail —— */}
        <div
          ref={desktopScrollRef}
          onScroll={handleDesktopScroll}
          className="hidden md:block h-full px-0 py-2 overflow-y-auto overflow-x-hidden sidebar-scroll-none"
          style={{ msOverflowStyle: "none", scrollbarWidth: "none" }}
        >
          <div className="flex flex-col items-center gap-3 pb-20 [--cell:60px]">
            <button
              type="button"
              onClick={() => setShowAllTools(true)}
              className="shrink-0 w-[var(--cell)]"
              title="View All Tools"
            >
              <div
                className={`w-[var(--cell)] h-[var(--cell)] rounded-lg flex items-center justify-center transition-colors ${
                  showAllTools ? "bg-primary/10 text-primary" : "bg-gray-100 hover:bg-gray-200 text-gray-600"
                }`}
              >
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                  />
                </svg>
              </div>
              <p className="text-[10px] font-medium text-center leading-tight mt-1 line-clamp-2 text-gray-700">
                View All Tools
              </p>
            </button>

            {items.map((item) => {
              const isActive = isCatalogNavActive(item, currentToolId);
              const content = (
                <div className="shrink-0 w-[var(--cell)]">
                  <div className="relative">
                    <div
                      className={`relative w-[var(--cell)] h-[var(--cell)] rounded-lg overflow-hidden bg-gray-100 ${
                        isActive ? "ring-4 ring-primary ring-offset-0" : ""
                      }`}
                    >
                      {item.poster ? (
                        <img
                          src={item.poster}
                          alt={item.title}
                          className="absolute inset-0 h-full w-full object-cover object-top"
                        />
                      ) : (
                        <span className="flex h-full w-full items-center justify-center text-sm font-bold text-gray-300">
                          {item.title.slice(0, 1)}
                        </span>
                      )}
                    </div>
                    {item.badge === "free" && (
                      <div className="absolute -top-[7px] -right-[7px] z-10 bg-[#69C85A] text-white text-[8px] font-bold px-1.5 py-0.5 rounded">
                        FREE
                      </div>
                    )}
                  </div>
                  <p
                    className={`text-[10px] font-medium text-center leading-tight mt-1 line-clamp-2 ${
                      isActive ? "text-primary" : "text-gray-700"
                    }`}
                  >
                    {item.title}
                  </p>
                </div>
              );

              if (isActive) {
                return (
                  <div key={item.id} title={`${item.title} (active)`}>
                    {content}
                  </div>
                );
              }

              return (
                <Link
                  key={item.id}
                  href={item.href}
                  title={`Open ${item.title}`}
                  onClick={() => trackSwitch(item)}
                >
                  {content}
                </Link>
              );
            })}
          </div>
        </div>

        {!dockMobileToBottom && (
          <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white via-white/80 to-transparent z-[1] hidden md:block" />
        )}
      </div>

      <AllToolsModal open={showAllTools} onClose={() => setShowAllTools(false)} />

      <style jsx>{`
        .sidebar-scroll-none::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </>
  );
}
